import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Code, FileCode, FolderOpen, Copy, Check, RefreshCw, Search, GitBranch, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import AppMapViewer from "./AppMapViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CodeViewer() {
  const [activeMainTab, setActiveMainTab] = useState("map");
  const [structure, setStructure] = useState({ backend: [], frontend: [] });
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedLines, setHighlightedLines] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const fetchStructure = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/code/structure`);
      setStructure(res.data);
    } catch (error) {
      console.error("Error fetching structure:", error);
      toast.error("Eroare la încărcarea structurii");
    }
  }, []);

  useEffect(() => {
    fetchStructure();
  }, [fetchStructure]);

  const handleDownloadCode = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/code/download`, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename=(.+)/);
      const filename = match ? match[1] : "e-tesa-source.zip";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Codul sursă a fost descărcat!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Eroare la descărcarea codului sursă");
    } finally {
      setDownloading(false);
    }
  };

  const fetchFile = async (path) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/code/file`, { params: { path } });
      setSelectedFile(path);
      setFileContent(res.data.content);
      setHighlightedLines([]);
      setSearchTerm("");
    } catch (error) {
      console.error("Error fetching file:", error);
      toast.error("Eroare la încărcarea fișierului");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(fileContent);
    setCopied(true);
    toast.success("Cod copiat în clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSearch = () => {
    if (!searchTerm || !fileContent) return;
    
    const lines = fileContent.split('\n');
    const matches = [];
    lines.forEach((line, idx) => {
      if (line.toLowerCase().includes(searchTerm.toLowerCase())) {
        matches.push(idx + 1);
      }
    });
    setHighlightedLines(matches);
    
    if (matches.length > 0) {
      toast.success(`${matches.length} potriviri găsite`);
    } else {
      toast.info("Nicio potrivire găsită");
    }
  };

  const getTotalLines = (files) => files.reduce((sum, f) => sum + f.lines, 0);

  const renderFileTree = (files, type) => (
    <div className="space-y-1">
      {files.map((file) => (
        <div
          key={file.path}
          onClick={() => fetchFile(file.path)}
          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
            selectedFile === file.path 
              ? 'bg-teal-100 border-l-4 border-teal-500' 
              : 'hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <FileCode className={`w-4 h-4 flex-shrink-0 ${type === 'backend' ? 'text-blue-500' : 'text-green-500'}`} />
            <span className="text-sm truncate">{file.path.split('/').pop()}</span>
          </div>
          <Badge variant="outline" className="text-xs ml-2">
            {file.lines} linii
          </Badge>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Download Button - prominent */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-teal-50 to-cyan-50">
        <CardContent className="py-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Download className="w-5 h-5 text-teal-600" />
              Descarcă Codul Sursă
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">Salvează o copie completă a codului (backend + frontend) ca arhivă ZIP</p>
          </div>
          <Button
            data-testid="download-source-code-btn"
            onClick={handleDownloadCode}
            disabled={downloading}
            className="bg-teal-600 hover:bg-teal-700 text-white px-6"
          >
            {downloading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Se descarcă...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Descarcă ZIP
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Main Tabs - Mapare vs Cod */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="map" className="flex items-center gap-2">
            <GitBranch className="w-4 h-4" />
            Mapare Aplicație
          </TabsTrigger>
          <TabsTrigger value="code" className="flex items-center gap-2">
            <Code className="w-4 h-4" />
            Cod Sursă
          </TabsTrigger>
        </TabsList>
        
        {/* Tab Mapare Aplicație */}
        <TabsContent value="map" className="mt-4">
          <AppMapViewer />
        </TabsContent>
        
        {/* Tab Cod Sursă */}
        <TabsContent value="code" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Code className="w-6 h-6 text-teal-600" />
                  Vizualizare Cod Sursă
                </h2>
                <p className="text-sm text-slate-500">Explorează și analizează codul aplicației</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchStructure}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reîncarcă
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* File Tree */}
              <Card className="lg:col-span-1 border-0 shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    Structură Fișiere
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="backend" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-2">
                      <TabsTrigger value="backend" className="text-xs">
                        Backend ({getTotalLines(structure.backend)})
                      </TabsTrigger>
                      <TabsTrigger value="frontend" className="text-xs">
                        Frontend ({getTotalLines(structure.frontend)})
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="backend">
                      <ScrollArea className="h-[400px]">
                        {renderFileTree(structure.backend, 'backend')}
                      </ScrollArea>
                    </TabsContent>
                    <TabsContent value="frontend">
                      <ScrollArea className="h-[400px]">
                        {renderFileTree(structure.frontend, 'frontend')}
                      </ScrollArea>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              {/* Code Viewer */}
              <Card className="lg:col-span-3 border-0 shadow-lg">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileCode className="w-4 h-4" />
                      {selectedFile || "Selectează un fișier"}
                    </CardTitle>
                    {selectedFile && (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Input
                            placeholder="Caută în cod..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            className="h-8 w-40 text-xs"
                          />
                          <Button variant="outline" size="sm" onClick={handleSearch} className="h-8 px-2">
                            <Search className="w-3 h-3" />
                          </Button>
                        </div>
                        <Button variant="outline" size="sm" onClick={copyToClipboard} className="h-8">
                          {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                          {copied ? "Copiat" : "Copiază"}
                        </Button>
                      </div>
                    )}
                  </div>
                  {highlightedLines.length > 0 && (
                    <div className="text-xs text-teal-600 mt-1">
                      Linii găsite: {highlightedLines.slice(0, 10).join(', ')}{highlightedLines.length > 10 ? '...' : ''}
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center h-[500px]">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                    </div>
                  ) : selectedFile ? (
                    <ScrollArea className="h-[500px] border rounded-lg bg-slate-950">
                      <pre className="p-4 text-xs font-mono">
                        {fileContent.split('\n').map((line, idx) => (
                          <div
                            key={`line-${idx}`}
                            className={`flex ${highlightedLines.includes(idx + 1) ? 'bg-yellow-500/30' : ''}`}
                          >
                            <span className="w-12 text-right pr-4 text-slate-500 select-none border-r border-slate-700 mr-4">
                              {idx + 1}
                            </span>
                            <code className="text-slate-200 whitespace-pre">{line || ' '}</code>
                          </div>
                        ))}
                      </pre>
                    </ScrollArea>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[500px] text-slate-400">
                      <Code className="w-16 h-16 mb-4 opacity-50" />
                      <p>Selectează un fișier din listă pentru a vedea codul</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Summary Stats */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-slate-50 to-slate-100">
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{structure.backend.length}</p>
                    <p className="text-xs text-slate-500">Fișiere Backend</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{getTotalLines(structure.backend)}</p>
                    <p className="text-xs text-slate-500">Linii Backend</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{structure.frontend.length}</p>
                    <p className="text-xs text-slate-500">Fișiere Frontend</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{getTotalLines(structure.frontend)}</p>
                    <p className="text-xs text-slate-500">Linii Frontend</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
